import React, { forwardRef } from 'react';
import { cn } from '@/utils/utils';
import { lureoButtonClass, type LureoButtonVariant } from './lureoButtonClass';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { LureoButtonTooltipPosition } from '@/types/tooltipTypes';

export type { LureoButtonVariant };

type ButtonAttributes = Pick<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'type' | 'id' | 'name' | 'form' | 'tabIndex' | 'role' | 'aria-label' | 'aria-pressed' | 'aria-current' | 'aria-expanded' | 'aria-haspopup' | 'aria-controls' | 'aria-describedby'
>;

interface LureoButtonProps extends ButtonAttributes {
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseDown?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseUp?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseEnter?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseLeave?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  /** Disables it and says why on hover in place of its tooltip, even with suppressTooltip: a disabled button takes no hover, so a span round it shows the reason. */
  disabledReason?: string | null;
  /** The span's layout in a row, for a button that fills it (flex-1); the button then fills the span. */
  wrapperClassName?: string;
  /** What it says on hover, as a themed tooltip. */
  tooltipContent?: string;
  tooltipPosition?: LureoButtonTooltipPosition;
  tooltipOffset?: number;
  /**
   * secondary is the primary action (the call to action); outline-2 the secondary action; danger destroys; menu is an
   * editor rail button; outline-3 is the unselected half of a solid/outline-3 toggle; no-outline a quiet button.
   */
  variant?: LureoButtonVariant;
  isSelected?: boolean;
  justify?: 'start' | 'center' | 'end';
  /** When true, render only the button (no Tooltip wrapper). Use when the button is inside a controlled Tooltip. */
  suppressTooltip?: boolean;
  'data-tutorial-anchor'?: string;
}

const justifyClasses = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
} as const;

const LureoButton = forwardRef<HTMLButtonElement, LureoButtonProps>((props, ref) => {
  const {
    onClick,
    onMouseDown,
    onMouseUp,
    children,
    className,
    disabled,
    disabledReason,
    wrapperClassName,
    tooltipContent,
    tooltipPosition,
    tooltipOffset = 10,
    variant = 'primary',
    isSelected = false,
    justify = 'center',
    suppressTooltip = false,
    // A plain button unless it submits its form
    type = 'button',
    ...restProps
  } = props;

  // Merged, so a class the caller passes replaces the base one it conflicts with
  const allClasses = cn(justifyClasses[justify], lureoButtonClass(variant, isSelected), className, disabledReason && 'pointer-events-none', disabledReason && wrapperClassName && 'w-full');

  const button = (
    <button
      ref={ref}
      type={type}
      onClick={onClick}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      disabled={disabled || !!disabledReason}
      className={allClasses}
      {...restProps}
    >
      {children}
    </button>
  );

  if (suppressTooltip && !disabledReason) {
    return button;
  }

  const tip = disabledReason || tooltipContent;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {disabledReason ? <span className={cn('inline-flex shrink-0 cursor-not-allowed', wrapperClassName)}>{button}</span> : button}
      </TooltipTrigger>
      {tip && (
        <TooltipContent
          sideOffset={tooltipOffset}
          side={tooltipPosition}
        >
          {tip}
        </TooltipContent>
      )}
    </Tooltip>
  );
});

export default LureoButton;
